import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ORIGIN } from '../../api/baseUrl';
import {
  getAdminUsers,
  getTeams,
  getRoles,
  previewReport,
  getReportRecipients,
  simulateBulkOwnership,
  simulateRoleChange,
  getSlackHealth,
  getUserDiagnostics,
  retryDelivery,
  sendReport,
  getCustomersList,
  getProductsList,
  getUserOwnerships,
  setUserOwnerships,
  getOwnershipsByType,
  getAdminUser,
  createAdminUser,
  assignRole,
  revokeRole,
  getSchedulerPreview,
  schedulerSendAll,
  type AdminUser,
  type BulkOwnershipResult,
  type RoleChangeResult,
} from '../../api/adminClient';
import clsx from 'clsx';

type SimulationType = 'report' | 'ownership' | 'role' | 'synthetic' | 'diagnostics';

export default function AdminSimulator() {
  const [activeTab, setActiveTab] = useState<SimulationType>('report');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">DevTools</h1>
        <p className="text-gray-500 mt-1">Preview changes, generate test data, diagnostics, and simulate user actions</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {[
            { key: 'report', label: 'Report Preview', icon: '📊' },
            { key: 'ownership', label: 'Ownership Changes', icon: '👥' },
            { key: 'role', label: 'Role Changes', icon: '🔐' },
            { key: 'synthetic', label: 'Test Data Generator', icon: '🧬' },
            { key: 'diagnostics', label: 'Delivery Diagnostics', icon: '🔍' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as SimulationType)}
              className={clsx(
                'flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'report' && <ReportPreviewSimulator />}
          {activeTab === 'ownership' && <OwnershipSimulator />}
          {activeTab === 'role' && <RoleChangeSimulator />}
          {activeTab === 'synthetic' && <SyntheticDataSimulator />}
          {activeTab === 'diagnostics' && <DeliveryDiagnostics />}
        </div>
      </div>
    </div>
  );
}

function ReportPreviewSimulator() {
  const [userId, setUserId] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: recipients } = useQuery({
    queryKey: ['report-recipients', 'all'],
    queryFn: () => getReportRecipients('all'),
  });

  const selectedUser = recipients?.find((r) => r.user_id === userId);
  const reportType = selectedUser?.scope_type || 'product';

  const previewMutation = useMutation({
    mutationFn: () => previewReport(userId, reportType, weekOffset),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendReport(userId, reportType, previewMutation.data?.stats || {}, previewMutation.data?.content || ''),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative" ref={userDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
          <input
            type="text"
            value={userSearch}
            onChange={(e) => { setUserSearch(e.target.value); setShowUserDropdown(true); if (!e.target.value) setUserId(''); }}
            onFocus={() => setShowUserDropdown(true)}
            placeholder="Search by name or email..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          {showUserDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {(recipients || [])
                .filter((r) => {
                  const q = userSearch.toLowerCase();
                  return !q || r.user_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
                })
                .map((r) => (
                  <button
                    key={r.user_id}
                    onClick={() => { setUserId(r.user_id); setUserSearch(r.user_name); setShowUserDropdown(false); previewMutation.reset(); sendMutation.reset(); }}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2',
                      userId === r.user_id && 'bg-primary-50 text-primary-700'
                    )}
                  >
                    <span className="font-medium">{r.user_name}</span>
                    {r.scope_type ? (
                      <span className={clsx(
                        'px-1.5 py-0.5 rounded text-xs',
                        r.scope_type === 'product' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      )}>{r.scope_type}</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-400">no scope</span>
                    )}
                    <span className="text-gray-400 ml-auto text-xs">{r.email}</span>
                  </button>
                ))}
              {recipients && recipients.filter((r) => {
                const q = userSearch.toLowerCase();
                return !q || r.user_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
              }).length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No matching users</div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Week</label>
          <select
            value={weekOffset}
            onChange={(e) => setWeekOffset(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value={0}>This Week</option>
            <option value={-1}>Last Week</option>
            <option value={-2}>2 Weeks Ago</option>
            <option value={-3}>3 Weeks Ago</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={() => previewMutation.mutate()}
            disabled={!userId || !selectedUser?.scope_type || previewMutation.isPending}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {previewMutation.isPending ? 'Loading...' : 'Preview Report'}
          </button>
          {userId && !selectedUser?.scope_type && (
            <span className="text-xs text-amber-600 ml-2">No report scope assigned</span>
          )}
        </div>
      </div>

      {/* Preview Result */}
      {previewMutation.data && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">
              {previewMutation.data.report_type} Report for {previewMutation.data.user_name}
            </h3>
            <span className="text-sm text-gray-500">{previewMutation.data.week_label}</span>
          </div>
          <pre className="bg-white border border-gray-200 rounded-lg p-4 text-sm overflow-x-auto whitespace-pre-wrap">
            {previewMutation.data.content}
          </pre>
          <div className="mt-4 flex items-center justify-between">
            {previewMutation.data.stats && (
              <div className="flex gap-4 text-sm text-gray-600">
                {Object.entries(previewMutation.data.stats).map(([key, value]) => (
                  <span key={key}>
                    {key.replace('_', ' ')}: <strong>{String(value)}</strong>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              {sendMutation.isSuccess && (
                <span className="text-sm text-green-600">Sent successfully</span>
              )}
              {sendMutation.isError && (
                <span className="text-sm text-red-600">
                  {(sendMutation.error as Error)?.message || 'Send failed'}
                </span>
              )}
              <button
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
              >
                {sendMutation.isPending ? 'Sending...' : 'Send to User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewMutation.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {previewMutation.error instanceof Error ? previewMutation.error.message : 'Preview failed'}
        </div>
      )}

      {/* Scheduler Preview Section */}
      <div className="border-t border-gray-200 pt-6">
        <SchedulerPreviewSection weekOffset={weekOffset} />
      </div>
    </div>
  );
}

function SchedulerPreviewSection({ weekOffset }: { weekOffset: number }) {
  const [showPreview, setShowPreview] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  const previewQuery = useQuery({
    queryKey: ['scheduler-preview', weekOffset],
    queryFn: () => getSchedulerPreview(weekOffset),
    enabled: showPreview,
  });

  const sendAllMutation = useMutation({
    mutationFn: () => schedulerSendAll(weekOffset, selectedUserIds.size > 0 ? [...selectedUserIds] : undefined),
    onSuccess: () => {
      // Refresh preview to show updated delivery statuses
      previewQuery.refetch();
    },
  });

  const recipients = previewQuery.data || [];
  const withData = recipients.filter(r => r.has_data);
  const withoutData = recipients.filter(r => !r.has_data);

  const toggleUser = (userId: string) => {
    const next = new Set(selectedUserIds);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setSelectedUserIds(next);
  };

  const selectAllWithData = () => {
    setSelectedUserIds(new Set(withData.map(r => r.user_id)));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Simulate Scheduler Run</h3>
          <p className="text-xs text-gray-400">Preview what the weekly scheduler would send, then trigger a batch send</p>
        </div>
        <button
          onClick={() => { setShowPreview(true); previewQuery.refetch(); }}
          disabled={previewQuery.isFetching}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
        >
          {previewQuery.isFetching ? 'Loading...' : showPreview ? 'Refresh' : 'Preview Recipients'}
        </button>
      </div>

      {showPreview && previewQuery.data && (
        <>
          {/* Summary */}
          <div className="flex gap-4 text-sm">
            <span className="text-gray-500">{recipients.length} recipients</span>
            <span className="text-green-600">{withData.length} with data</span>
            <span className="text-amber-600">{withoutData.length} empty</span>
          </div>

          {/* Recipients table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.size === withData.length && withData.length > 0}
                      onChange={(e) => e.target.checked ? selectAllWithData() : setSelectedUserIds(new Set())}
                      className="rounded border-gray-300 text-primary-600"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Delivery Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recipients.map((r) => (
                  <tr key={`${r.user_id}-${r.report_type}`} className={clsx(!r.has_data && 'opacity-50')}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(r.user_id)}
                        onChange={() => toggleUser(r.user_id)}
                        disabled={!r.has_data}
                        className="rounded border-gray-300 text-primary-600 disabled:opacity-30"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-900">{r.user_name}</span>
                      {r.scope_all && <span className="ml-1.5 px-1 py-0.5 rounded text-[10px] bg-primary-100 text-primary-700">ALL</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={clsx('px-1.5 py-0.5 rounded text-xs', r.report_type === 'product' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}>
                        {r.report_type}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.has_data
                        ? <span className="text-green-600 text-xs font-medium">Has data</span>
                        : <span className="text-amber-500 text-xs">No data</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {r.current_delivery_status
                          ? <span className={clsx('px-1.5 py-0.5 rounded text-xs font-medium',
                              r.current_delivery_status === 'delivered' ? 'bg-green-100 text-green-800' :
                              r.current_delivery_status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            )}>{r.current_delivery_status}</span>
                          : <span className="text-gray-400 text-xs">Not sent</span>}
                        {sendAllMutation.data?.results.find(res => res.user_name === r.user_name && res.report_type === r.report_type) && (() => {
                          const res = sendAllMutation.data!.results.find(res => res.user_name === r.user_name && res.report_type === r.report_type)!;
                          return res.error
                            ? <span className="text-amber-500 text-[10px]" title={res.error}>({res.success ? 'stored' : 'error'})</span>
                            : null;
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Send All button */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => sendAllMutation.mutate()}
              disabled={sendAllMutation.isPending || (selectedUserIds.size === 0 && withData.length === 0)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {sendAllMutation.isPending
                ? 'Sending...'
                : selectedUserIds.size > 0
                  ? `Send to ${selectedUserIds.size} Selected`
                  : `Send All (${withData.length})`}
            </button>
            {selectedUserIds.size > 0 && (
              <button
                onClick={() => setSelectedUserIds(new Set())}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear selection
              </button>
            )}
          </div>

          {/* Send results */}
          {sendAllMutation.data && (
            <div className={clsx('rounded-lg p-4 border', sendAllMutation.data.failed > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200')}>
              <h4 className={clsx('font-medium mb-2', sendAllMutation.data.failed > 0 ? 'text-yellow-800' : 'text-green-800')}>
                Batch Send Complete: {sendAllMutation.data.succeeded} succeeded, {sendAllMutation.data.failed} failed
              </h4>
              <div className="space-y-1 text-sm">
                {sendAllMutation.data.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={r.success ? 'text-green-600' : 'text-red-600'}>{r.success ? '+' : '-'}</span>
                    <span className="text-gray-700">{r.user_name}</span>
                    <span className="text-gray-400">({r.report_type})</span>
                    {r.error && <span className="text-red-500 text-xs">{r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {sendAllMutation.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              {sendAllMutation.error instanceof Error ? sendAllMutation.error.message : 'Send failed'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OwnershipSimulator() {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<'assign' | 'remove'>('assign');
  const [entityType, setEntityType] = useState<'customer' | 'product'>('customer');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<Array<{ id: string; name: string }>>([]);
  const [entitySearch, setEntitySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [applyProgress, setApplyProgress] = useState<{ current: number; total: number; errors: string[] } | null>(null);
  const entityDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(e.target as Node)) {
        setShowEntityDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selections when entity type changes
  useEffect(() => {
    setSelectedEntities([]);
    setSelectedUsers([]);
    setEntitySearch('');
    simulateMutation.reset();
  }, [entityType]);

  const { data: users } = useQuery({
    queryKey: ['report-recipients', 'all'],
    queryFn: () => getReportRecipients('all'),
  });

  const { data: entities } = useQuery({
    queryKey: ['entities', entityType],
    queryFn: () => entityType === 'customer' ? getCustomersList() : getProductsList(),
  });

  const { data: allOwnerships } = useQuery({
    queryKey: ['ownerships-by-type', entityType],
    queryFn: () => getOwnershipsByType(entityType),
  });

  // Build lookup maps: entity → owners, user → owned entities
  const entityOwners = new Map<string, Array<{ user_id: string; user_name: string }>>();
  const userOwnedEntities = new Map<string, Set<string>>();
  for (const o of allOwnerships || []) {
    if (!entityOwners.has(o.entity_id)) entityOwners.set(o.entity_id, []);
    entityOwners.get(o.entity_id)!.push({ user_id: o.user_id, user_name: o.user_name });
    if (!userOwnedEntities.has(o.user_id)) userOwnedEntities.set(o.user_id, new Set());
    userOwnedEntities.get(o.user_id)!.add(o.entity_id);
  }

  const filteredUsers = (users || []).filter((u) => {
    const matchesScope = u.scope_type === entityType;
    const q = userSearch.toLowerCase();
    const matchesSearch = !q || u.user_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    return matchesScope && matchesSearch;
  });

  const filteredEntities = (entities || []).filter((e) => {
    const q = entitySearch.toLowerCase();
    const notSelected = !selectedEntities.some((s) => s.id === e.id);
    return notSelected && (!q || e.name.toLowerCase().includes(q));
  });

  const simulateMutation = useMutation({
    mutationFn: () =>
      simulateBulkOwnership({
        action,
        user_ids: selectedUsers,
        entity_type: entityType,
        entity_ids: selectedEntities.map((e) => e.id),
      }),
  });

  const handleApply = async () => {
    const result = simulateMutation.data;
    if (!result) return;

    const usersToUpdate = result.by_user.filter(
      (u) => u.changes.some((c) => c.change !== 'unchanged')
    );
    setApplyProgress({ current: 0, total: usersToUpdate.length, errors: [] });

    const errors: string[] = [];
    for (let i = 0; i < usersToUpdate.length; i++) {
      const userSim = usersToUpdate[i];
      try {
        // Get current ownerships for this user
        const currentOwnerships = await getUserOwnerships(userSim.user_id);
        const currentForType = currentOwnerships
          .filter((o) => o.entity_type === entityType)
          .map((o) => o.entity_id);
        const otherTypes = currentOwnerships
          .filter((o) => o.entity_type !== entityType)
          .map((o) => ({ entity_type: o.entity_type, entity_id: o.entity_id }));

        let updatedForType: string[];
        if (action === 'assign') {
          const toAdd = userSim.changes.filter((c) => c.change === 'add').map((c) => {
            return selectedEntities.find((e) => e.name === c.entity_name)?.id || '';
          }).filter(Boolean);
          updatedForType = [...new Set([...currentForType, ...toAdd])];
        } else {
          const toRemove = new Set(
            userSim.changes.filter((c) => c.change === 'remove').map((c) => {
              return selectedEntities.find((e) => e.name === c.entity_name)?.id || '';
            }).filter(Boolean)
          );
          updatedForType = currentForType.filter((id) => !toRemove.has(id));
        }

        const newOwnerships = [
          ...otherTypes,
          ...updatedForType.map((id) => ({ entity_type: entityType, entity_id: id })),
        ];
        await setUserOwnerships(userSim.user_id, newOwnerships);
      } catch (err) {
        errors.push(`${userSim.user_name}: ${err instanceof Error ? err.message : 'Failed'}`);
      }
      setApplyProgress({ current: i + 1, total: usersToUpdate.length, errors });
    }

    // Invalidate all caches affected by ownership changes
    queryClient.invalidateQueries({ queryKey: ['report-recipients'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'ownerships'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    queryClient.invalidateQueries({ queryKey: ['customers-list'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['products-list'] });
    queryClient.invalidateQueries({ queryKey: ['ownerships-by-type'] });
  };

  return (
    <div className="space-y-6">
      {/* Controls row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
          <select
            value={action}
            onChange={(e) => { setAction(e.target.value as 'assign' | 'remove'); simulateMutation.reset(); setApplyProgress(null); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="assign">Assign Ownership</option>
            <option value="remove">Remove Ownership</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
          <select
            value={entityType}
            onChange={(e) => { setEntityType(e.target.value as 'customer' | 'product'); setApplyProgress(null); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="customer">Customer</option>
            <option value="product">Product</option>
          </select>
        </div>
      </div>

      {/* Entity selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select {entityType === 'customer' ? 'Customers' : 'Products'}
        </label>
        {/* Selected tags with current owners */}
        {selectedEntities.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {selectedEntities.map((entity) => {
              const owners = entityOwners.get(entity.id) || [];
              return (
                <div key={entity.id} className="flex items-center gap-2 px-2 py-1.5 bg-primary-50 text-sm rounded-lg border border-primary-200">
                  <span className="font-medium text-primary-700">{entity.name}</span>
                  {owners.length > 0 ? (
                    <span className="text-xs text-gray-500">
                      owned by: {owners.map((o) => o.user_name).join(', ')}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">no current owners</span>
                  )}
                  <button
                    onClick={() => { setSelectedEntities(selectedEntities.filter((e) => e.id !== entity.id)); simulateMutation.reset(); setApplyProgress(null); }}
                    className="text-primary-400 hover:text-primary-600 ml-auto"
                  >
                    &times;
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="relative" ref={entityDropdownRef}>
          <input
            type="text"
            value={entitySearch}
            onChange={(e) => { setEntitySearch(e.target.value); setShowEntityDropdown(true); }}
            onFocus={() => setShowEntityDropdown(true)}
            placeholder={`Search ${entityType === 'customer' ? 'customers' : 'products'}...`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          {showEntityDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredEntities.length > 0 ? filteredEntities.slice(0, 50).map((entity) => (
                <button
                  key={entity.id}
                  onClick={() => { setSelectedEntities([...selectedEntities, entity]); setEntitySearch(''); simulateMutation.reset(); setApplyProgress(null); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                >
                  {entity.name}
                </button>
              )) : (
                <div className="px-3 py-2 text-sm text-gray-400">No matching {entityType === 'customer' ? 'customers' : 'products'}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Users
          <span className="text-gray-400 font-normal ml-1">
            (showing {entityType}-scoped users{selectedUsers.length > 0 ? ` · ${selectedUsers.length} selected` : ''})
          </span>
        </label>
        <input
          type="text"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Filter users..."
          className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
        <div className="border border-gray-300 rounded-lg p-2 max-h-48 overflow-y-auto">
          {filteredUsers.length > 0 ? filteredUsers.map((user) => {
            const ownedCount = userOwnedEntities.get(user.user_id)?.size || 0;
            const ownsSelected = selectedEntities.filter((e) => userOwnedEntities.get(user.user_id)?.has(e.id)).length;
            return (
              <label key={user.user_id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.user_id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers([...selectedUsers, user.user_id]);
                    } else {
                      setSelectedUsers(selectedUsers.filter((id) => id !== user.user_id));
                    }
                    simulateMutation.reset();
                    setApplyProgress(null);
                  }}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{user.user_name}</span>
                <span className="text-xs text-gray-400 ml-auto flex items-center gap-2">
                  {selectedEntities.length > 0 && ownsSelected > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      owns {ownsSelected}/{selectedEntities.length} selected
                    </span>
                  )}
                  <span>{ownedCount} {entityType}{ownedCount !== 1 ? 's' : ''}</span>
                  <span className="hidden md:inline">{user.email}</span>
                </span>
              </label>
            );
          }) : (
            <div className="p-2 text-sm text-gray-400">No {entityType}-scoped users found</div>
          )}
        </div>
      </div>

      {/* Preview button */}
      <button
        onClick={() => { setApplyProgress(null); simulateMutation.mutate(); }}
        disabled={selectedUsers.length === 0 || selectedEntities.length === 0 || simulateMutation.isPending}
        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
      >
        {simulateMutation.isPending ? 'Simulating...' : 'Preview Changes'}
      </button>

      {/* Simulation Result */}
      {simulateMutation.data && (
        <OwnershipPreviewResult
          result={simulateMutation.data}
          onApply={handleApply}
          applyProgress={applyProgress}
        />
      )}

      {simulateMutation.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {simulateMutation.error instanceof Error ? simulateMutation.error.message : 'Simulation failed'}
        </div>
      )}
    </div>
  );
}

function OwnershipPreviewResult({
  result,
  onApply,
  applyProgress,
}: {
  result: BulkOwnershipResult;
  onApply: () => void;
  applyProgress: { current: number; total: number; errors: string[] } | null;
}) {
  const isApplying = applyProgress !== null && applyProgress.current < applyProgress.total;
  const isApplied = applyProgress !== null && applyProgress.current === applyProgress.total;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">
            Preview: {result.action === 'assign' ? 'Assign' : 'Remove'} Ownership
          </h3>
          <p className="text-sm text-gray-500">
            {result.total_changes} change{result.total_changes !== 1 ? 's' : ''} across {result.by_user.length} user{result.by_user.length !== 1 ? 's' : ''}
          </p>
        </div>
        {result.total_changes > 0 && !isApplied && (
          <button
            onClick={onApply}
            disabled={isApplying}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {isApplying ? `Applying ${applyProgress!.current}/${applyProgress!.total}...` : 'Apply Changes'}
          </button>
        )}
      </div>

      {/* Progress bar */}
      {applyProgress && (
        <div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={clsx('h-2 rounded-full transition-all', applyProgress.errors.length > 0 ? 'bg-yellow-500' : 'bg-green-500')}
              style={{ width: `${(applyProgress.current / applyProgress.total) * 100}%` }}
            />
          </div>
          {isApplied && (
            <div className={clsx('mt-2 text-sm font-medium', applyProgress.errors.length > 0 ? 'text-yellow-700' : 'text-green-700')}>
              {applyProgress.errors.length > 0
                ? `Completed with ${applyProgress.errors.length} error${applyProgress.errors.length !== 1 ? 's' : ''}`
                : `All ${applyProgress.total} user${applyProgress.total !== 1 ? 's' : ''} updated successfully`}
            </div>
          )}
          {applyProgress.errors.length > 0 && (
            <div className="mt-2 space-y-1">
              {applyProgress.errors.map((err, i) => (
                <div key={i} className="text-sm text-red-600">{err}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Per-user breakdown */}
      <div className="space-y-3">
        {result.by_user.map((user) => {
          const hasChanges = user.changes.some((c) => c.change !== 'unchanged');
          return (
            <div key={user.user_id} className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{user.user_name}</span>
                <span className="text-sm text-gray-500">
                  {user.current_ownerships} → {user.after_ownerships}
                </span>
              </div>
              {hasChanges ? (
                <div className="space-y-1">
                  {user.changes.filter((c) => c.change !== 'unchanged').map((change, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className={clsx(
                        'px-1.5 py-0.5 rounded text-xs font-medium',
                        change.change === 'add' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      )}>
                        {change.change === 'add' ? '+' : '−'}
                      </span>
                      <span className="text-gray-700">{change.entity_name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400">No changes (already {result.action === 'assign' ? 'assigned' : 'unassigned'})</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Map resource:action permissions to human-readable descriptions
function describePermission(perm: string): string {
  const [resource, action] = perm.split(':');
  const resourceLabels: Record<string, string> = {
    organizations: 'Organizations', admin_users: 'Admin Users', users: 'Users',
    roles: 'Roles', ownership: 'Ownership', reports: 'Reports',
    settings: 'Settings', audit: 'Audit Logs', simulator: 'DevTools',
  };
  const actionLabels: Record<string, string> = {
    create: 'Create', read: 'View', update: 'Edit', delete: 'Delete',
    import: 'Import', export: 'Export', bulk: 'Bulk Operations', generate: 'Generate', use: 'Use',
  };
  return `${actionLabels[action] || action} ${resourceLabels[resource] || resource}`;
}

function RoleChangeSimulator() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isEmployee, setIsEmployee] = useState(false); // true if selected user is an employee without admin account
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const [promotingStatus, setPromotingStatus] = useState<{ promoting: boolean; error?: string } | null>(null);
  const [addRoles, setAddRoles] = useState<string[]>([]);
  const [removeRoles, setRemoveRoles] = useState<string[]>([]);
  const [applyStatus, setApplyStatus] = useState<{ applying: boolean; success?: boolean; error?: string } | null>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch admin users and all employees
  const { data: adminUsers } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => getAdminUsers(),
  });

  const { data: employees } = useQuery({
    queryKey: ['report-recipients', 'all'],
    queryFn: () => getReportRecipients('all'),
  });

  const { data: roles } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: getRoles,
  });

  // Build combined user list: admin users + employees not yet in admin
  const adminEmails = new Set((adminUsers || []).map((u) => u.email));
  const combinedUsers = [
    ...(adminUsers || []).map((u) => ({
      id: u.id,
      name: u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.email,
      email: u.email,
      isAdmin: true,
    })),
    ...(employees || [])
      .filter((e) => !adminEmails.has(e.email))
      .map((e) => ({
        id: e.user_id,
        name: e.user_name,
        email: e.email,
        isAdmin: false,
      })),
  ];

  const filteredCombinedUsers = combinedUsers.filter((u) => {
    const q = userSearch.toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  // Fetch selected user's current roles (only for admin users)
  const { data: selectedUserDetail } = useQuery({
    queryKey: ['admin', 'user-detail', userId],
    queryFn: () => getAdminUser(userId),
    enabled: !!userId && !isEmployee,
  });

  const currentRoleIds = new Set(
    (selectedUserDetail?.roles as Array<{ role_id?: string; id?: string }>)?.map((r) => r.role_id || r.id) || []
  );

  // Reset selections when user changes
  useEffect(() => {
    setAddRoles([]);
    setRemoveRoles([]);
    simulateMutation.reset();
    setApplyStatus(null);
    setPromotingStatus(null);
  }, [userId]);

  const simulateMutation = useMutation({
    mutationFn: () =>
      simulateRoleChange(
        userId,
        addRoles.length > 0 ? addRoles : undefined,
        removeRoles.length > 0 ? removeRoles : undefined
      ),
  });

  const handleSelectUser = (user: { id: string; name: string; email: string; isAdmin: boolean }) => {
    setUserId(user.id);
    setUserSearch(user.name);
    setIsEmployee(!user.isAdmin);
    setShowUserDropdown(false);
  };

  const handlePromoteEmployee = async () => {
    const employee = combinedUsers.find((u) => u.id === userId);
    if (!employee) return;
    setPromotingStatus({ promoting: true });
    try {
      const [firstName, ...lastParts] = employee.name.split(' ');
      const newAdmin = await createAdminUser({
        email: employee.email,
        password: crypto.randomUUID(), // Temporary password — user will need to reset
        first_name: firstName,
        last_name: lastParts.join(' ') || undefined,
      });
      // Switch to the new admin user ID
      setUserId(newAdmin.id);
      setIsEmployee(false);
      setPromotingStatus(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (err) {
      setPromotingStatus({ promoting: false, error: err instanceof Error ? err.message : 'Failed to create admin account' });
    }
  };

  const handleApply = async () => {
    setApplyStatus({ applying: true });
    try {
      for (const roleId of addRoles) {
        await assignRole(userId, roleId);
      }
      for (const roleId of removeRoles) {
        await revokeRole(userId, roleId);
      }
      setApplyStatus({ applying: false, success: true });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-detail', userId] });
    } catch (err) {
      setApplyStatus({ applying: false, error: err instanceof Error ? err.message : 'Failed to apply role changes' });
    }
  };

  // Filter out super_admin — managed via Mission Control, not this admin panel
  const orgRoles = (roles || []).filter((r) => r.name !== 'super_admin');
  // Roles the user doesn't have yet (available to add)
  const availableToAdd = orgRoles.filter((r) => !currentRoleIds.has(r.id));
  // Roles the user currently has (available to remove)
  const availableToRemove = orgRoles.filter((r) => currentRoleIds.has(r.id));

  const selectedUser = combinedUsers.find((u) => u.id === userId);

  // Only show dropdown after 2+ characters typed
  const showResults = userSearch.length >= 2;
  const visibleUsers = showResults ? filteredCombinedUsers.slice(0, 50) : [];

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [userSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showUserDropdown || visibleUsers.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(highlightedIndex + 1, visibleUsers.length - 1);
      setHighlightedIndex(next);
      // Scroll into view
      dropdownListRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(highlightedIndex - 1, 0);
      setHighlightedIndex(next);
      dropdownListRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectUser(visibleUsers[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowUserDropdown(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* User search */}
      <div className="relative" ref={userDropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
        <input
          type="text"
          value={userSearch}
          onChange={(e) => { setUserSearch(e.target.value); setShowUserDropdown(true); if (!e.target.value) { setUserId(''); setIsEmployee(false); } }}
          onFocus={() => { if (userSearch.length >= 2) setShowUserDropdown(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Type at least 2 characters to search..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
        {showUserDropdown && showResults && (
          <div ref={dropdownListRef} className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {visibleUsers.length > 0 ? visibleUsers.map((user, index) => (
              <button
                key={`${user.isAdmin ? 'admin' : 'emp'}-${user.id}`}
                onClick={() => handleSelectUser(user)}
                className={clsx(
                  'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2',
                  index === highlightedIndex ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50',
                  userId === user.id && index !== highlightedIndex && 'bg-gray-50'
                )}
              >
                <span className="font-medium">{user.name}</span>
                {user.isAdmin ? (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700">admin</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">employee</span>
                )}
                <span className="text-gray-400 ml-auto text-xs">{user.email}</span>
              </button>
            )) : (
              <div className="px-3 py-2 text-sm text-gray-400">No matching users</div>
            )}
          </div>
        )}
      </div>

      {/* Employee promotion prompt */}
      {userId && isEmployee && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800 mb-3">
            <strong>{selectedUser?.name}</strong> is an employee without an admin account. Create an admin account to assign roles.
          </p>
          <button
            onClick={handlePromoteEmployee}
            disabled={promotingStatus?.promoting}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {promotingStatus?.promoting ? 'Creating Admin Account...' : 'Create Admin Account'}
          </button>
          {promotingStatus?.error && (
            <div className="mt-2 text-sm text-red-600">{promotingStatus.error}</div>
          )}
        </div>
      )}

      {/* Current roles badges */}
      {userId && !isEmployee && selectedUserDetail && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Roles</label>
          <div className="flex flex-wrap gap-2">
            {(selectedUserDetail.roles as Array<{ role_name?: string; display_name?: string }> || []).length > 0 ? (
              (selectedUserDetail.roles as Array<{ role_name?: string; display_name?: string }>).map((r, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700 border border-primary-200">
                  {r.display_name || r.role_name}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-400">No roles assigned</span>
            )}
          </div>
        </div>
      )}

      {/* Role checkboxes */}
      {userId && !isEmployee && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Roles to Add
              {availableToAdd.length === 0 && <span className="text-gray-400 font-normal ml-1">(user has all roles)</span>}
            </label>
            <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto">
              {availableToAdd.length > 0 ? availableToAdd.map((role) => (
                <label key={role.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addRoles.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) { setAddRoles([...addRoles, role.id]); }
                      else { setAddRoles(addRoles.filter((id) => id !== role.id)); }
                      simulateMutation.reset(); setApplyStatus(null);
                    }}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">{role.display_name}</span>
                  {role.description && <span className="text-xs text-gray-400 ml-auto">{role.description}</span>}
                </label>
              )) : (
                <div className="p-2 text-sm text-gray-400">All roles already assigned</div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Roles to Remove
              {availableToRemove.length === 0 && <span className="text-gray-400 font-normal ml-1">(no roles to remove)</span>}
            </label>
            <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto">
              {availableToRemove.length > 0 ? availableToRemove.map((role) => (
                <label key={role.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={removeRoles.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) { setRemoveRoles([...removeRoles, role.id]); }
                      else { setRemoveRoles(removeRoles.filter((id) => id !== role.id)); }
                      simulateMutation.reset(); setApplyStatus(null);
                    }}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">{role.display_name}</span>
                  {role.description && <span className="text-xs text-gray-400 ml-auto">{role.description}</span>}
                </label>
              )) : (
                <div className="p-2 text-sm text-gray-400">No roles currently assigned</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview button */}
      <button
        onClick={() => { setApplyStatus(null); simulateMutation.mutate(); }}
        disabled={!userId || isEmployee || (addRoles.length === 0 && removeRoles.length === 0) || simulateMutation.isPending}
        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
      >
        {simulateMutation.isPending ? 'Analyzing...' : 'Preview Impact'}
      </button>

      {/* Impact Result */}
      {simulateMutation.data && (
        <RoleImpactResult
          result={simulateMutation.data}
          onApply={handleApply}
          applyStatus={applyStatus}
        />
      )}

      {simulateMutation.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {simulateMutation.error instanceof Error ? simulateMutation.error.message : 'Simulation failed'}
        </div>
      )}
    </div>
  );
}

function RoleImpactResult({
  result,
  onApply,
  applyStatus,
}: {
  result: RoleChangeResult;
  onApply: () => void;
  applyStatus: { applying: boolean; success?: boolean; error?: string } | null;
}) {
  const hasChanges = result.gained_permissions.length > 0 || result.lost_permissions.length > 0;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Header with Apply button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Impact Analysis: {result.user_name}</h3>
          <p className="text-sm text-gray-500">
            {hasChanges
              ? `${result.gained_permissions.length} permission${result.gained_permissions.length !== 1 ? 's' : ''} gained, ${result.lost_permissions.length} lost`
              : 'No permission changes'}
          </p>
        </div>
        {hasChanges && !applyStatus?.success && (
          <button
            onClick={onApply}
            disabled={applyStatus?.applying}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {applyStatus?.applying ? 'Applying...' : 'Apply Role Changes'}
          </button>
        )}
      </div>

      {/* Apply feedback */}
      {applyStatus?.success && (
        <div className="text-sm font-medium text-green-700 bg-green-50 rounded-lg p-3 border border-green-200">
          Role changes applied successfully
        </div>
      )}
      {applyStatus?.error && (
        <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3 border border-red-200">
          {applyStatus.error}
        </div>
      )}

      {/* Gained permissions */}
      {result.gained_permissions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-green-700 mb-2">Permissions Gained ({result.gained_permissions.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {result.gained_permissions.map((perm) => (
              <span key={perm} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-lg" title={perm}>
                {describePermission(perm)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Lost permissions */}
      {result.lost_permissions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-red-700 mb-2">Permissions Lost ({result.lost_permissions.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {result.lost_permissions.map((perm) => (
              <span key={perm} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-lg" title={perm}>
                {describePermission(perm)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Affected capabilities */}
      {result.affected_capabilities.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Impact Summary</h4>
          <ul className="space-y-1">
            {result.affected_capabilities.map((cap, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                <span className="text-amber-500">!</span> {cap}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Permission summary */}
      <div className="text-xs text-gray-400 border-t border-gray-200 pt-3">
        Total permissions: {result.current_permissions.length} current → {result.after_permissions.length} after
      </div>
    </div>
  );
}

// Test Data Generator
function SyntheticDataSimulator() {
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState('');
  const [updates, setUpdates] = useState(50);
  const [weeks, setWeeks] = useState(4);

  const { data: teams } = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: () => getTeams(),
  });

  // Fetch existing test data history
  const { data: testHistory } = useQuery({
    queryKey: ['test-data-history'],
    queryFn: async () => {
      const response = await fetch(`${API_ORIGIN}/api/admin/simulator/synthetic-data?limit=100`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch test data');
      return data.data as {
        reports: Array<{ id: string; author: string; customer: string; products: string[]; engagement: string; sentiment: string; notes: string; created_at: string }>;
        stats: { total_reports: string; total_updates: string; total_rollups: string };
      };
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_ORIGIN}/api/admin/simulator/synthetic-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, weeks, team_id: teamId || undefined }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate test data');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-data-history'] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_ORIGIN}/api/admin/simulator/synthetic-data`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to clear test data');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-data-history'] });
    },
  });

  const selectedTeam = teams?.find((t) => t.id === teamId);

  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-xl">!</span>
          <div>
            <h4 className="font-medium text-yellow-800">Test Data Only</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Generated reports are marked with [TEST] prefix. Team members submit reports about customers/products, then rollups are generated for the report pipeline.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
          <select
            value={teamId}
            onChange={(e) => { setTeamId(e.target.value); generateMutation.reset(); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">All teams</option>
            {teams?.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Report Count</label>
          <input
            type="number"
            min="1"
            max="500"
            value={updates}
            onChange={(e) => setUpdates(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Week Range</label>
          <select
            value={weeks}
            onChange={(e) => setWeeks(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            {[1, 2, 3, 4, 6, 8, 12].map((w) => (
              <option key={w} value={w}>{w} week{w !== 1 ? 's' : ''} back</option>
            ))}
          </select>
        </div>
      </div>

      {/* Preview line */}
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
        Will create ~<strong>{updates}</strong> reports from <strong>{selectedTeam?.name || 'all teams'}</strong> members across <strong>{weeks}</strong> week{weeks !== 1 ? 's' : ''}, then generate team rollups for the report pipeline
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {generateMutation.isPending ? 'Generating...' : 'Generate Test Reports'}
        </button>
        <button
          onClick={() => clearMutation.mutate()}
          disabled={clearMutation.isPending}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {clearMutation.isPending ? 'Clearing...' : 'Clear All Test Data'}
        </button>
      </div>

      {/* Generation Result */}
      {generateMutation.data && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">Test Data Generated</h4>
            <div className="text-sm text-green-700 space-y-1">
              <p><strong>{generateMutation.data.reports_created || 0}</strong> reports created across {weeks} week{weeks !== 1 ? 's' : ''}</p>
              <p><strong>{generateMutation.data.rollups_created || 0}</strong> team rollups generated</p>
              <p><strong>{generateMutation.data.updates_created || 0}</strong> raw updates created</p>
              <p className="text-green-600 mt-2">You can now preview reports in the Report Preview tab.</p>
            </div>
          </div>

          {/* Report details table */}
          {generateMutation.data.report_details?.length > 0 && (
            <GeneratedReportsTable reports={generateMutation.data.report_details} />
          )}
        </div>
      )}

      {/* Clear Result */}
      {clearMutation.data && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">Test Data Cleared</h4>
          <p className="text-sm text-blue-700">
            Removed {clearMutation.data.deleted_count || 0} test records
          </p>
        </div>
      )}

      {/* Errors */}
      {(generateMutation.error || clearMutation.error) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {((generateMutation.error || clearMutation.error) as Error)?.message}
        </div>
      )}

      {/* Test Data History */}
      {testHistory && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700">Test Data in System</h4>
            <div className="flex gap-3 text-xs text-gray-500">
              <span>{testHistory.stats.total_reports} reports</span>
              <span>{testHistory.stats.total_rollups} rollups</span>
              <span>{testHistory.stats.total_updates} updates</span>
            </div>
          </div>
          {testHistory.reports.length > 0 ? (
            <TestDataHistory reports={testHistory.reports} />
          ) : (
            <div className="text-sm text-gray-400 bg-gray-50 rounded-lg p-4 text-center">
              No test data in the system. Generate some above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TestDataHistory({ reports }: { reports: Array<{ id: string; author: string; customer: string; products: string[]; engagement: string; sentiment: string; notes: string; created_at: string }> }) {
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);

  // Group by ISO week
  const byWeek = new Map<string, typeof reports>();
  for (const r of reports) {
    const date = new Date(r.created_at);
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    const key = `W${weekNum}-${d.getUTCFullYear()}`;
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key)!.push(r);
  }

  const sortedWeeks = [...byWeek.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  const sentimentIcon = (s: string) => s === 'positive' ? '🟢' : s === 'negative' ? '🔴' : '🟡';
  const engagementColor = (e: string) => {
    const colors: Record<string, string> = { 'pre-sales': 'bg-blue-100 text-blue-700', 'post-sales': 'bg-green-100 text-green-700', growth: 'bg-purple-100 text-purple-700', threat: 'bg-red-100 text-red-700' };
    return colors[e] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {sortedWeeks.map(([weekLabel, weekReports]) => (
        <div key={weekLabel}>
          <button
            onClick={() => setExpandedWeek(expandedWeek === weekLabel ? null : weekLabel)}
            className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-100"
          >
            <span>{weekLabel} ({weekReports.length} reports)</span>
            <span className="text-gray-400">{expandedWeek === weekLabel ? '−' : '+'}</span>
          </button>
          {expandedWeek === weekLabel && (
            <div className="divide-y divide-gray-100">
              {weekReports.map((r) => (
                <div key={r.id} className="px-4 py-2 text-sm flex items-start gap-3">
                  <span className="mt-0.5">{sentimentIcon(r.sentiment)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="px-1 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-700 leading-none">TEST</span>
                      <span className="font-medium text-gray-900">{r.author}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-gray-700">{r.customer}</span>
                      {r.products.length > 0 && (
                        <>
                          <span className="text-gray-400">re:</span>
                          <span className="text-gray-700">{r.products.join(', ')}</span>
                        </>
                      )}
                      <span className={clsx('px-1.5 py-0.5 rounded text-xs', engagementColor(r.engagement))}>{r.engagement}</span>
                    </div>
                    <p className="text-gray-500 text-xs">{r.notes}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function GeneratedReportsTable({ reports }: { reports: Array<{ author: string; customer: string; product: string; engagement: string; sentiment: string; notes: string; week: number; year: number }> }) {
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);

  // Group by week
  const byWeek = new Map<string, typeof reports>();
  for (const r of reports) {
    const key = `W${r.week}-${r.year}`;
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key)!.push(r);
  }

  const sortedWeeks = [...byWeek.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  const sentimentIcon = (s: string) => s === 'positive' ? '🟢' : s === 'negative' ? '🔴' : '🟡';
  const engagementColor = (e: string) => {
    const colors: Record<string, string> = { 'pre-sales': 'bg-blue-100 text-blue-700', 'post-sales': 'bg-green-100 text-green-700', growth: 'bg-purple-100 text-purple-700', threat: 'bg-red-100 text-red-700' };
    return colors[e] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-700">Generated Reports ({reports.length})</h4>
      </div>
      {sortedWeeks.map(([weekLabel, weekReports]) => (
        <div key={weekLabel}>
          <button
            onClick={() => setExpandedWeek(expandedWeek === weekLabel ? null : weekLabel)}
            className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-100"
          >
            <span>{weekLabel} ({weekReports.length} reports)</span>
            <span className="text-gray-400">{expandedWeek === weekLabel ? '−' : '+'}</span>
          </button>
          {expandedWeek === weekLabel && (
            <div className="divide-y divide-gray-100">
              {weekReports.map((r, i) => (
                <div key={i} className="px-4 py-2 text-sm flex items-start gap-3">
                  <span className="mt-0.5">{sentimentIcon(r.sentiment)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-gray-900">{r.author}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-gray-700">{r.customer}</span>
                      <span className="text-gray-400">re:</span>
                      <span className="text-gray-700">{r.product}</span>
                      <span className={clsx('px-1.5 py-0.5 rounded text-xs', engagementColor(r.engagement))}>{r.engagement}</span>
                    </div>
                    <p className="text-gray-500 text-xs">{r.notes}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}



// ============================================================================
// DELIVERY DIAGNOSTICS
// ============================================================================

function DeliveryDiagnostics() {
  const [selectedUserId, setSelectedUserId] = useState('');

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => getAdminUsers(),
  });

  const { data: slackHealth, isLoading: slackLoading } = useQuery({
    queryKey: ['slack-health'],
    queryFn: getSlackHealth,
  });

  const { data: diagnostics, isLoading: diagLoading } = useQuery({
    queryKey: ['user-diagnostics', selectedUserId],
    queryFn: () => getUserDiagnostics(selectedUserId),
    enabled: !!selectedUserId,
  });

  const retryMutation = useMutation({
    mutationFn: retryDelivery,
  });

  return (
    <div className="space-y-6">
      {/* Slack Health */}
      <div className="rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Slack Connection</h3>
        {slackLoading ? (
          <div className="text-sm text-gray-500">Checking...</div>
        ) : slackHealth ? (
          <div className="flex items-center gap-3">
            <span className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
              slackHealth.connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            )}>
              <span className={clsx('w-2 h-2 rounded-full', slackHealth.connected ? 'bg-green-500' : 'bg-red-500')} />
              {slackHealth.connected ? 'Connected' : 'Disconnected'}
            </span>
            {slackHealth.connected && (
              <span className="text-sm text-gray-500">
                {slackHealth.bot_name} on {slackHealth.workspace}
              </span>
            )}
            {slackHealth.error && (
              <span className="text-sm text-red-600">{slackHealth.error}</span>
            )}
          </div>
        ) : null}
      </div>

      {/* User Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
        <select
          value={selectedUserId}
          onChange={(e) => { setSelectedUserId(e.target.value); retryMutation.reset(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Choose a user...</option>
          {(users as AdminUser[] | undefined)?.map((u) => (
            <option key={u.id} value={u.id}>{u.first_name ? `${u.first_name} ${u.last_name || ''}` : u.email}</option>
          ))}
        </select>
      </div>

      {/* Diagnostics Results */}
      {selectedUserId && (
        diagLoading ? (
          <div className="text-sm text-gray-500">Loading diagnostics...</div>
        ) : diagnostics?.user ? (
          <div className="space-y-4">
            {/* User Info */}
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">User Info</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Name:</span> {diagnostics.user.name}</div>
                <div><span className="text-gray-500">Email:</span> {diagnostics.user.email}</div>
                <div>
                  <span className="text-gray-500">Scope:</span>{' '}
                  {diagnostics.user.scope_type ? (
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">{diagnostics.user.scope_type}</span>
                  ) : (
                    <span className="text-gray-400">none</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Slack ID:</span>{' '}
                  {diagnostics.user.slack_id || <span className="text-gray-400">not set</span>}
                </div>
              </div>
            </div>

            {/* Slack Validation */}
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Slack Validation</h3>
              {diagnostics.slack_valid === null ? (
                <span className="text-sm text-gray-400">No Slack ID to validate</span>
              ) : diagnostics.slack_valid ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 rounded-full bg-green-500" /> Valid
                </span>
              ) : (
                <div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <span className="w-2 h-2 rounded-full bg-red-500" /> Invalid
                  </span>
                  {diagnostics.slack_error && (
                    <p className="text-sm text-red-600 mt-1">{diagnostics.slack_error}</p>
                  )}
                </div>
              )}
            </div>

            {/* Ownership */}
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Ownership</h3>
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Customers:</span>{' '}
                  <span className="font-medium">{diagnostics.ownership_counts.customers}</span>
                </div>
                <div>
                  <span className="text-gray-500">Products:</span>{' '}
                  <span className="font-medium">{diagnostics.ownership_counts.products}</span>
                </div>
              </div>
            </div>

            {/* Recent Deliveries */}
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Deliveries</h3>
              {diagnostics.recent_deliveries.length === 0 ? (
                <span className="text-sm text-gray-400">No delivery history</span>
              ) : (
                <div className="space-y-2">
                  {diagnostics.recent_deliveries.map((d) => (
                    <div key={d.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center gap-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          d.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          d.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        )}>
                          {d.status}
                        </span>
                        <span className="text-gray-700">{d.report_type}</span>
                        <span className="text-gray-400">W{d.week} {d.year}</span>
                        {d.error_message && (
                          <span className="text-red-500 text-xs truncate max-w-[200px]" title={d.error_message}>
                            {d.error_message}
                          </span>
                        )}
                      </div>
                      {d.status === 'failed' && (
                        <button
                          onClick={() => retryMutation.mutate(d.id)}
                          disabled={retryMutation.isPending}
                          className="px-2 py-1 text-xs font-medium bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                        >
                          {retryMutation.isPending ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {retryMutation.isSuccess && (
                <div className="mt-2 text-sm text-green-600">Delivery retried successfully.</div>
              )}
              {retryMutation.isError && (
                <div className="mt-2 text-sm text-red-600">
                  Retry failed: {(retryMutation.error as Error)?.message || 'Unknown error'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">User not found</div>
        )
      )}
    </div>
  );
}

